import { off, on } from 'common/tools/dom/events';
import getMobileOS from 'common/tools/dom/getMobileOS';
import { navigateTo } from 'common/tools/dom/navigateTo';
import readAttribute from 'common/tools/dom/readAttribute';

// Delay before assuming the app is not installed and redirecting to the store.
export const FALLBACK_DELAY = 1500;
// Beyond this elapsed time the timer was most likely suspended while the app
// was in the foreground: the user is coming back, so we must not redirect.
const MAX_ELAPSED = 2000;

/**
 * On click, try to open the app through the deep link. If the page stays
 * visible (the app did not take focus), redirect to the store to let the user
 * download the app.
 */
const bindFallback = (link: HTMLAnchorElement, storeUrl: string) => {
  on(link, 'click', (event: Event) => {
    event.preventDefault();

    // Guard against a double-click stacking timers and listeners.
    if (link.dataset.pending) {
      return;
    }
    link.dataset.pending = '1';

    const deepLink = link.getAttribute('href') ?? '';
    const start = Date.now();
    let timer = 0;

    function cleanup() {
      delete link.dataset.pending;
      window.clearTimeout(timer);
      off(document, 'visibilitychange', onHidden);
      off(window, 'pagehide', cleanup);
      off(window, 'blur', cleanup);
    }

    function onHidden() {
      // The app took over and the page went to the background: no fallback.
      if (document.visibilityState === 'hidden') {
        cleanup();
      }
    }

    on(document, 'visibilitychange', onHidden);
    on(window, 'pagehide', cleanup);
    on(window, 'blur', cleanup);

    timer = window.setTimeout(() => {
      cleanup();

      if (
        document.visibilityState === 'visible' &&
        Date.now() - start < MAX_ELAPSED
      ) {
        navigateTo(storeUrl);
      }
    }, FALLBACK_DELAY);

    navigateTo(deepLink);
  });
};

export default function initInAppRead() {
  const wrappers = document.querySelectorAll<HTMLElement>('.js-in-app-read');

  if (!wrappers.length) {
    return;
  }

  const os = getMobileOS();

  // The button is mobile-only: nothing to fall back to on desktop.
  if (os === 'other') {
    return;
  }

  const storeAttr =
    os === 'ios' ? 'data-ios-store-url' : 'data-android-store-url';

  wrappers.forEach(wrapper => {
    const storeUrl = readAttribute<string>(wrapper, storeAttr);
    const link = wrapper.querySelector('a');

    if (!storeUrl || !link) {
      return;
    }

    bindFallback(link, storeUrl);
  });
}
